import React, { useState, useEffect, useRef, useCallback } from 'react';
import './Projects.css';
import projectsData from '../data/projects.json';
import { useCanvasAnimation } from '../hooks/useCanvasAnimation';
import { buildContactPrefillHash, navigateToContactPrefill } from '../constants';

const importAll = (r) => {
  let images = {};
  r.keys().forEach((item) => {
    const fileName = item.replace('./', '');
    images[fileName] = r(item);
    const fileNameNoExt = fileName.replace(/\.(png|jpe?g|svg|webp)$/i, '');
    images[fileNameNoExt] = r(item);
  });
  return images;
};

const images = importAll(require.context('../assets/projects-slides', false, /\.(png|jpe?g|svg|webp)$/));

const getImage = (imagePath, projectName) => {
  if (images[imagePath]) {
    return images[imagePath].default || images[imagePath];
  }
  if (projectName && images[projectName]) {
    return images[projectName].default || images[projectName];
  }
  const fileNameNoExt = imagePath.replace(/\.(png|jpe?g|svg|webp)$/i, '');
  if (images[fileNameNoExt]) {
    return images[fileNameNoExt].default || images[fileNameNoExt];
  }
  return imagePath;
};

function Projects() {
  const { canvasRef, containerRef } = useCanvasAnimation();
  const [activeTab, setActiveTab] = useState('present');
  const [activeCardId, setActiveCardId] = useState(null);
  const [projects, setProjects] = useState([]);
  const sliderRef = useRef(null);
  const dragRef = useRef({ isDragging: false, startX: 0, scrollLeft: 0, distance: 0 });

  useEffect(() => {
    setProjects(projectsData.projects);
    const filtered = projectsData.projects.filter(p => p.active);
    if (filtered.length > 0) setActiveCardId(filtered[0].id);
  }, []);

  const projectsToShow = projects.filter(
    project => activeTab === 'present' ? project.active : !project.active
  );

  useEffect(() => {
    if (projectsToShow.length > 0) {
      setActiveCardId(projectsToShow[0].id);
    }
  }, [activeTab, projects.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll active card into view
  const scrollToCard = useCallback((id) => {
    if (!sliderRef.current) return;
    const card = sliderRef.current.querySelector(`[data-project-id="${id}"]`);
    if (!card) return;

    const slider = sliderRef.current;
    const sliderRect = slider.getBoundingClientRect();
    const cardLeft = card.offsetLeft;
    const cardWidth = card.offsetWidth;
    const sliderWidth = sliderRect.width;
    const target = cardLeft - (sliderWidth / 2) + (cardWidth / 2);
    const maxScroll = slider.scrollWidth - sliderWidth;

    slider.scrollTo({
      left: Math.max(0, Math.min(target, maxScroll)),
      behavior: 'smooth'
    });
  }, []);

  useEffect(() => {
    if (activeCardId !== null) {
      const timer = setTimeout(() => scrollToCard(activeCardId), 150);
      return () => clearTimeout(timer);
    }
  }, [activeCardId, scrollToCard]);

  // Navigate to prev/next card
  const navigateCard = (direction) => {
    const currentIdx = projectsToShow.findIndex(p => p.id === activeCardId);
    if (currentIdx === -1) return;
    const nextIdx = direction === 'left'
      ? Math.max(0, currentIdx - 1)
      : Math.min(projectsToShow.length - 1, currentIdx + 1);
    setActiveCardId(projectsToShow[nextIdx].id);
  };

  const handleCardClick = (projectId) => {
    if (dragRef.current.distance < 8) {
      setActiveCardId(projectId);
    }
  };

  const handleConnectClick = (e, connectCta) => {
    e.stopPropagation();
    e.preventDefault();
    navigateToContactPrefill({
      service: connectCta?.service,
      message: connectCta?.message
    });
  };

  // Clean drag handlers, no scroll locking, just horizontal drag
  const handlePointerDown = (e) => {
    if (!sliderRef.current) return;
    // Only handle left mouse button or touch
    if (e.type === 'mousedown' && e.button !== 0) return;

    const pageX = e.type === 'touchstart' ? e.touches[0].pageX : e.pageX;
    dragRef.current = {
      isDragging: true,
      startX: pageX,
      scrollLeft: sliderRef.current.scrollLeft,
      distance: 0
    };
    sliderRef.current.style.cursor = 'grabbing';
    sliderRef.current.style.scrollBehavior = 'auto';
  };

  const handlePointerMove = (e) => {
    if (!dragRef.current.isDragging || !sliderRef.current) return;
    if (e.type === 'mousemove') e.preventDefault();

    const pageX = e.type === 'touchmove' ? e.touches[0].pageX : e.pageX;
    const walk = pageX - dragRef.current.startX;
    dragRef.current.distance = Math.abs(walk);
    sliderRef.current.scrollLeft = dragRef.current.scrollLeft - walk;
  };

  const handlePointerUp = () => {
    if (!sliderRef.current) return;
    dragRef.current.isDragging = false;
    sliderRef.current.style.cursor = 'grab';
    sliderRef.current.style.scrollBehavior = 'smooth';

    // Reset distance after short delay (so click handler can check it)
    setTimeout(() => { dragRef.current.distance = 0; }, 100);
  };

  return (
    <section id="projects" className="projects" ref={containerRef}>
      <canvas ref={canvasRef} className="projects-canvas" />
      <div className="section-container">
        <div className="section-header">
          <h2 className="section-title">Projects & Communities</h2>
          <div className="section-divider"></div>
          <p className="section-subtitle">
            Present and past projects I've participated in
          </p>
        </div>

        <div className="projects-tabs">
          <button
            className={`tab-button ${activeTab === 'present' ? 'active' : ''}`}
            onClick={() => setActiveTab('present')}
            aria-label="View present projects"
          >
            Active
          </button>
          <button
            className={`tab-button ${activeTab === 'past' ? 'active' : ''}`}
            onClick={() => setActiveTab('past')}
            aria-label="View past projects"
          >
            Past
          </button>
        </div>

        <div className="swipe-hint">
          Swipe to see more projects
        </div>

        <div className="projects-slider-container">
          <button
            className="slider-nav-btn slider-nav-left"
            onClick={() => navigateCard('left')}
            aria-label="Previous project"
          >
            ‹
          </button>

          <div
            className="projects-slider"
            ref={sliderRef}
            onMouseDown={handlePointerDown}
            onMouseMove={handlePointerMove}
            onMouseUp={handlePointerUp}
            onMouseLeave={handlePointerUp}
            onTouchStart={handlePointerDown}
            onTouchMove={handlePointerMove}
            onTouchEnd={handlePointerUp}
            style={{ cursor: 'grab' }}
          >
            <div className="slider-track">
              {projectsToShow.map((project) => {
                const imageSrc = getImage(project.image, project.name);
                return (
                  <div
                    key={project.id}
                    className={`project-slider-card ${activeCardId === project.id ? 'active' : ''}${project.featured ? ' featured' : ''}`}
                    onClick={() => handleCardClick(project.id)}
                    data-project-id={project.id}
                  >
                    {project.featured && (
                      <div className="featured-badge">Featured</div>
                    )}
                    {imageSrc && (
                      <img
                        src={imageSrc}
                        alt={`${project.name} - ${project.role}`}
                        className="project-slider-image"
                        loading="lazy"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    )}
                    <div className="card-gradient-overlay"></div>
                    <div className="project-slider-content">
                      <h3 className="project-slider-name">{project.name}</h3>
                      <div className="project-role-badge">{project.role}</div>
                      <p className="project-slider-description">{project.description}</p>
                      {project.featured && project.featuredNote && activeCardId === project.id && (
                        <p className="project-featured-note">{project.featuredNote}</p>
                      )}
                      <div className="project-slider-tags">
                        {project.tags.map((tag, tagIndex) => (
                          <span key={tagIndex} className="project-slider-tag">{tag}</span>
                        ))}
                      </div>
                      {(project.link || project.connectCta) && (
                        <div className="project-slider-actions">
                          {project.link && (
                            <a
                              href={project.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="project-slider-link"
                              onClick={(e) => e.stopPropagation()}
                            >
                              Learn More →
                            </a>
                          )}
                          {project.connectCta && (
                            <a
                              href={buildContactPrefillHash({
                                service: project.connectCta.service,
                                message: project.connectCta.message
                              })}
                              className="project-slider-link project-slider-link-connect"
                              onClick={(e) => handleConnectClick(e, project.connectCta)}
                            >
                              {project.connectCta.label} →
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <button
            className="slider-nav-btn slider-nav-right"
            onClick={() => navigateCard('right')}
            aria-label="Next project"
          >
            ›
          </button>
        </div>
      </div>
    </section>
  );
}

export default Projects;
